'use client';

import { MapComponents } from '@components/BlockMapper';
import ShopsFeaturedArticles from '@components/CardGridWithMobileCarousel/ShopsFeaturedArticles';
import { getEntries } from '@data/preview';
import { addShopDataToContentBlocks } from '@util/addShopDataToContentBlocks';
import getShopUrlPrepend from '@util/getShopUrlPrepend';
import { getCorrectHeader } from '@util/locale';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useEffect, useState } from 'react';

interface SellingWithUsPageProps {
  page: any;
  locale: string;
  pageType?: 'shopHomepage' | 'shopGroupHomepage';
  contentfulTagIds?: string | null;
  chimeData?: any;
  shopname?: string;
}

const SellingWithUsPage: FC<SellingWithUsPageProps> = ({
  page,
  locale,
  pageType,
  contentfulTagIds,
  chimeData,
  shopname,
}) => {
  const t = useTranslations();
  const shopUrlPrepend = getShopUrlPrepend();
  const pageData = page;

  const americasSellingWithUsContentfulId = '3R1Ou4bWmCTsNFFYROf1gM'; // The contentful id for the default "Selling With Us" page (which will be used for the Americas site in this case)
  const isAmericasPage = pageData?.sys?.id === americasSellingWithUsContentfulId; // Check if the pageData being passed is the default "Selling With Us" page
  let [americasPageData, setAmericasPageData] = useState<any | null>(null); // Set up state for storing the default "Selling With Us" page data
  const [isDefaultBlogs, setIsDefaultBlogs] = useState<boolean>(true);
  const fetchAmericasPageData = async () => {
    // function for fetching the default "Selling With Us" page data
    try {
      let pageData = await getEntries(americasSellingWithUsContentfulId, locale);
      setAmericasPageData(pageData);
    } catch (e) {
      console.log(e);
    }
  };
  useEffect(() => {
    if (!isAmericasPage) {
      // if the pageData is not the default "Selling With Us" page, fetch the default "Selling With Us" page data
      fetchAmericasPageData();
    }
  }, [locale]);

  //create empty array to put all components into
  const componentsPrimary: ReactNode[] = [];
  const componentsSecondary: ReactNode[] = [];
  const fields = pageData?.fields;
  //
  //
  //Interior Header Component
  //
  // if the pageData is not the default "Selling With Us" page and doesn't have a Interior Header Component in the shop Contentful content (and also a check to make sure the default "Selling With Us" page data has a Interior Header component)
  // the 'else if' will cover if this is the pageData that is the default "Selling With Us" page and it has a Interior Header Component
  if (!isAmericasPage && !fields?.hero?.fields && americasPageData?.fields?.hero) {
    componentsPrimary[0] = americasPageData?.fields?.hero;
  } else if (fields?.hero) {
    componentsPrimary[0] = fields?.hero;
  }
  //
  //
  //Get In Touch - Text and Image Block1
  //
  if (
    !isAmericasPage &&
    !fields?.textAndImageBlock1?.fields &&
    americasPageData?.fields?.textAndImageBlock1
  ) {
    componentsPrimary[1] = americasPageData?.fields?.textAndImageBlock1;
  } else if (fields?.textAndImageBlock1) {
    componentsPrimary[1] = fields?.textAndImageBlock1;
  }
  //
  //
  //MyHomeWorth Embed
  //
  if (fields && fields.myHomeWorthEmbed?.fields) {
    // skip this if the pageData doesn't have it (meaning don't use the default "Selling With Us" MyHomeWorth Embed when this isn't the America's "Selling With Us" page)
    const block = {
      ...fields.myHomeWorthEmbed,
      fields: {
        ...fields.myHomeWorthEmbed?.fields,
        async: true, // this is adding an additional field to the MyHomeWorth Embed block to tell it to load asynchronously
      },
    };
    componentsPrimary[2] = block;
  }
  //
  //
  //Home Valuation - Text And Image Block2
  //
  let homeValuationBlock;
  if (
    !isAmericasPage &&
    !fields?.textAndImageBlock2?.fields &&
    americasPageData?.fields?.textAndImageBlock2
  ) {
    homeValuationBlock = americasPageData?.fields?.textAndImageBlock2;
  } else if (fields?.textAndImageBlock2) {
    homeValuationBlock = fields?.textAndImageBlock2;
  }
  if (homeValuationBlock) {
    homeValuationBlock = {
      ...homeValuationBlock,
      fields: {
        ...homeValuationBlock.fields,
        cta: [
          {
            ...homeValuationBlock.fields.cta?.[0],
            fields: {
              ...homeValuationBlock.fields.cta?.[0].fields,
              url:
                pageType === 'shopHomepage' || pageType === 'shopGroupHomepage'
                  ? `${shopUrlPrepend}/our-advisors`
                  : `https://www.evrealestate.com/shops-and-advisors/advisor-locator`,
            },
          },
        ],
      },
    };
    componentsPrimary[3] = homeValuationBlock;
  }
  //
  //
  //The EV Difference Block - Three Column Content
  //
  if (
    !isAmericasPage &&
    !fields?.threeColumnContentBlock?.fields &&
    americasPageData?.fields?.threeColumnContentBlock
  ) {
    componentsPrimary[4] = americasPageData?.fields?.threeColumnContentBlock;
  } else if (fields?.threeColumnContentBlock) {
    componentsPrimary[4] = fields?.threeColumnContentBlock;
  }
  //
  //
  //Who We Are - Text and Image Block3
  //
  let whoWeAreBlock;
  if (
    !isAmericasPage &&
    !fields?.textAndImageBlock?.fields &&
    americasPageData?.fields?.textAndImageBlock3
  ) {
    whoWeAreBlock = americasPageData?.fields?.textAndImageBlock3;
  } else if (fields?.textAndImageBlock3) {
    whoWeAreBlock = fields?.textAndImageBlock3;
  }
  if ((pageType === 'shopHomepage' || pageType === 'shopGroupHomepage') && whoWeAreBlock) {
    //remove cta if shopIds are present
    whoWeAreBlock = {
      ...whoWeAreBlock,
      fields: {
        ...whoWeAreBlock.fields,
        cta: [
          {
            ...whoWeAreBlock.fields.cta?.[0],
            fields: {
              ...whoWeAreBlock.fields.cta?.[0].fields,
              url: `https://www.evrealestate.com/company/about-us`,
            },
          },
        ],
      },
    };
  }
  if (whoWeAreBlock) {
    componentsPrimary[5] = whoWeAreBlock;
  }
  //
  //
  //Seller's Guide - Text and Image Block4
  //
  if (
    !isAmericasPage &&
    !fields?.textAndImageBlock4?.fields &&
    americasPageData?.fields?.textAndImageBlock4
  ) {
    componentsPrimary[6] = americasPageData?.fields?.textAndImageBlock4;
  } else if (fields?.textAndImageBlock4) {
    componentsPrimary[6] = fields?.textAndImageBlock4;
  }
  //
  //
  //Buy With Us - Wide Text Image Block
  //
  let buyWithUsBlock;
  if (
    !isAmericasPage &&
    !fields?.wideTextImageBlock?.fields &&
    americasPageData?.fields?.wideTextImageBlock
  ) {
    buyWithUsBlock = americasPageData?.fields?.wideTextImageBlock;
  } else if (fields?.wideTextImageBlock) {
    buyWithUsBlock = fields?.wideTextImageBlock;
  }
  if (buyWithUsBlock) {
    buyWithUsBlock = {
      ...buyWithUsBlock,
      fields: {
        ...buyWithUsBlock.fields,
        links: [
          {
            ...buyWithUsBlock.fields?.links?.[0],
            fields: {
              ...buyWithUsBlock.fields?.links?.[0].fields,
              url:
                pageType === 'shopHomepage' || pageType === 'shopGroupHomepage'
                  ? `${shopUrlPrepend}/properties/our-listings`
                  : `/properties/our-listings`,
            },
          },
        ],
      },
    };
    componentsPrimary[7] = buyWithUsBlock;
  }

  const featuredArticlesHeaderProcessed = getCorrectHeader(fields?.blogHeader, locale);
  const featuredArticlesButtonTextProcessed = getCorrectHeader(
    fields?.viewAllArticlesButtonText,
    locale,
  );

  //
  //
  //Dynamic Block Content Area
  //Update components with shop specific data
  //
  const updatedDyanmicContentBlocks = addShopDataToContentBlocks({
    contentBlocks: fields?.dynamicContentBlocks,
    chimeId: chimeData?.chimeId,
    chimeType: chimeData?.type,
    contentfulTagIds: contentfulTagIds,
    pageData,
  });
  if (updatedDyanmicContentBlocks && updatedDyanmicContentBlocks.length > 0) {
    componentsSecondary.push(...updatedDyanmicContentBlocks);
  }

  //
  //
  //Contact Form Block
  //
  let updatedContactBlock;
  if (!isAmericasPage && !fields?.contact?.fields && americasPageData?.fields?.contact) {
    updatedContactBlock = americasPageData?.fields?.contact;
  } else if (fields?.contact) {
    updatedContactBlock = fields?.contact;
  }
  if (updatedContactBlock) {
    updatedContactBlock = {
      ...updatedContactBlock,
      fields: {
        ...updatedContactBlock.fields,
        chimeId: chimeData?.chimeId,
        chimeType: chimeData?.type,
      },
    };
    componentsSecondary.push(updatedContactBlock);
  }

  //remove any undefined elements in the primary components array
  const filteredComponentsPrimary = componentsPrimary.filter(function (el) {
    return el != null;
  });

  return (
    <main>
      {componentsPrimary && componentsPrimary.length > 0 && (
        <MapComponents blocks={filteredComponentsPrimary} />
      )}
      <ShopsFeaturedArticles
        blogHeader={
          featuredArticlesHeaderProcessed ? featuredArticlesHeaderProcessed : t('E&V Blog')
        }
        viewAllArticlesButtonText={
          featuredArticlesButtonTextProcessed
            ? featuredArticlesButtonTextProcessed
            : t('View All Articles')
        }
        contentfulTagIds={contentfulTagIds ?? ''}
        pageData={pageData}
        shopname={shopname}
      />

      {componentsSecondary && componentsSecondary.length > 0 && (
        <MapComponents blocks={componentsSecondary} />
      )}
    </main>
  );
};

export default SellingWithUsPage;
